import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Cookies } from 'react-cookie-consent';
import { CookieProvider } from './context/CookieContext';

import CookiesManager from './components/Cookies';
import Footer from './components/Footer';
import EmptyNavBar from './components/global/navbar/EmptyNavBar';
import NavBar from './components/global/navbar/NavBar';
import PrivateRoute from './components/PrivateRoute';
import Seo from './components/seo/Seo';
import Admin from './pages/Admin';
import Transaction from './pages/cart/CartPage';
import ConfirmEmail from './pages/ConfirmEmail';
import ForgotPassword from './pages/ForgotPassword';
import Home from './pages/home/Home';
import Login from './pages/Login';
import Order from './pages/odrer/Order';
import Offer from './pages/offer/Offer';
import ProducerPage from './pages/ProducerPage';
import ProductPage from './pages/ProductPage';
import Profile from './pages/profile/Profile';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Search from './pages/Search';
import TermsAndConditions from './pages/TermsAndConditions';
import About from './pages/About';
import Contact from './pages/Contact';
import { useHealth } from './context/HealthContext';
import Loading from './components/global/loading/Loading';
import { motion } from 'framer-motion';
import { useConfig } from './utils/useConfig';
import Return from './pages/return/Return';
import { initializeGA, trackPageView } from './utils/analytics';
import SitemapRedirect from './components/SitemapRedirect';

function App() {
  const location = useLocation();
  const { systemHealth } = useHealth();
  const config = useConfig();

  // Inicjalizacja narzędzi analitycznych tylko gdy mamy zgodę
  useEffect(() => {
    const analyticsConsent = Cookies.get('cookieconsent_analytics') === 'true';
    const marketingConsent = Cookies.get('cookieconsent_marketing') === 'true';

    if (analyticsConsent) {
      // Inicjalizacja Google Analytics
      initializeGA();
    }

    if (marketingConsent) {
      // Inicjalizacja narzędzi marketingowych, np. Facebook Pixel
      // przykład: initializeMarketing();
    }
  }, []);

  // Śledzenie zmiany strony
  useEffect(() => {
    if (systemHealth === true) {
      trackPageView(location.pathname + location.search);
    }
  }, [location.pathname, location.search, systemHealth]);

  return (
    <CookieProvider>
      <div>
        {/* Dodajemy domyślne SEO dla całej aplikacji */}
        <Seo 
          title="Strona główna"
          description={config.company.about.shortInfo || "Sklep internetowy z szeroką ofertą produktów"}        
          image={config.company.logo}
        />
        
        <CookiesManager />
        {location.pathname.startsWith('/koszyk') && systemHealth === true ? (
          <EmptyNavBar />
        ) : systemHealth === true ? (
          <NavBar />
        ) : null}
        {systemHealth === null && <Loading />}
        {systemHealth === false && (
          <motion.div
            className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-b from-gray-50 to-white px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="bg-white p-8 sm:p-12 rounded-2xl shadow-xl border border-gray-100 max-w-2xl w-full text-center"
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
                delay: 0.2,
              }}
            >
              <motion.div
                className="flex items-center justify-center mb-6"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 15,
                  delay: 0.4,
                }}
              >
                <div className="relative">
                  <div
                    className="absolute inset-0 rounded-full bg-primary/10 animate-ping"
                    style={{ animationDuration: '3s' }}
                  ></div>
                  <div className="relative bg-primary/5 p-4 rounded-full">
                    <img
                      src={config.company.logo}
                      alt="Logo"
                      className="sm:w-24 sm:h-24 w-16 h-16"
                    />
                  </div>
                </div>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl font-bold font-display uppercase text-gray-900 mb-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                {config.company.name}
              </motion.h1>

              <motion.div
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
                  Serwis jest aktualnie niedostępny
                </h2>

                <p className="text-gray-600">
                  Pracujemy nad ulepszeniem naszej platformy. Przepraszamy za
                  utrudnienia i zapraszamy wkrótce.
                </p>

                <div className="pt-4">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <div className="relative w-3 h-3">
                      <span className="absolute inset-0 bg-primary rounded-full animate-ping opacity-75"></span>
                      <span className="absolute inset-0 bg-primary rounded-full"></span>
                    </div>
                    Trwają prace techniczne
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="mt-8 pt-6 border-t border-gray-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <p className="text-sm text-gray-500">
                  Jeśli potrzebujesz pilnie się z nami skontaktować, napisz email
                  na adres:
                  <a
                    href={`mailto:${config.contact.email}`}
                    className="text-primary hover:underline ml-1"
                  >
                    {config.contact.email}
                  </a>
                </p>
              </motion.div>
            </motion.div>

            <motion.p
              className="text-gray-500 text-sm mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              © {new Date().getFullYear()} {config.company.name}. Wszelkie prawa
              zastrzeżone.
            </motion.p>
          </motion.div>
        )}
        {systemHealth === true && (
          <div className="px-1 md:px-4 ml-auto mr-auto w-full">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/sitemap.xml" element={<SitemapRedirect />} />
              <Route
                path="/admin/*"
                element={
                  <PrivateRoute checkRole={'Admin'}>
                    <Admin />
                  </PrivateRoute>
                }
              />
              <Route path="/logowanie" element={<Login />} />
              <Route path="/zapomniałem-hasła" element={<ForgotPassword />} />
              <Route path="/resetuj-haslo/:token" element={<ResetPassword />} />
              <Route path="/rejestracja" element={<Register />} />
              <Route path="/potwierdz-email/:token" element={<ConfirmEmail />} />
              <Route path="/produkt/:name" element={<ProductPage />} />
              <Route path="/producent/:name" element={<ProducerPage />} />
              <Route path="/oferta/:id/*" element={<Offer />} />
              <Route path="/szukaj" element={<Search />} />
              <Route path="/regulamin" element={<TermsAndConditions />} />
              <Route path="/koszyk/*" element={<Transaction />} />
              <Route path="/zamówienie/:id/*" element={<Order />} />
              <Route path="/zwrot/:id/*" element={<Return />} />
              <Route path="/o-nas" element={<About />} />
              <Route path="/kontakt" element={<Contact />} />
              <Route
                path="/profil/*"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
            </Routes>
          </div>
        )}
        <Footer />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="colored"
        />
      </div>
    </CookieProvider>
  );
}

export default App;
